import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export const useCondominiumStats = () => {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ["condominium-stats", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) {
        return {
          totalUnits: 0,
          totalProfiles: 0,
          pendingPackages: 0,
          totalAnnouncements: 0,
          openIncidents: 0,
          occupiedUnits: 0,
          monthlyPackages: 0
        };
      }

      // Buscar total de unidades
      const { count: totalUnits } = await supabase
        .from("units")
        .select("*", { count: "exact", head: true })
        .eq("condominium_id", profile.condominium_id);

      // Buscar total de moradores
      const { count: totalProfiles } = await supabase
        .from("profiles")
        .select("*", { count: "exact", head: true })
        .eq("condominium_id", profile.condominium_id)
        .eq("role", "morador");

      // Buscar encomendas pendentes
      const { count: pendingPackages } = await supabase
        .from("packages")
        .select("unit_id, units!inner(condominium_id)", { count: "exact", head: true })
        .eq("status", "pending")
        .eq("units.condominium_id", profile.condominium_id);

      // Buscar avisos
      const { count: totalAnnouncements } = await supabase
        .from("announcements")
        .select("author_id, profiles!inner(condominium_id)", { count: "exact", head: true })
        .eq("profiles.condominium_id", profile.condominium_id);

      // Buscar ocorrências abertas
      const { count: openIncidents } = await supabase
        .from("incidents")
        .select("*", { count: "exact", head: true })
        .eq("condominium_id", profile.condominium_id)
        .in("status", ["open", "in_progress"]);

      // Buscar unidades ocupadas
      const { count: occupiedUnits } = await supabase
        .from("units")
        .select("*", { count: "exact", head: true })
        .eq("condominium_id", profile.condominium_id)
        .not("owner_id", "is", null);

      // Buscar encomendas do mês
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { count: monthlyPackages } = await supabase
        .from("packages")
        .select("unit_id, units!inner(condominium_id)", { count: "exact", head: true })
        .eq("units.condominium_id", profile.condominium_id)
        .gte("created_at", startOfMonth.toISOString());

      return {
        totalUnits: totalUnits || 0,
        totalProfiles: totalProfiles || 0,
        pendingPackages: pendingPackages || 0,
        totalAnnouncements: totalAnnouncements || 0,
        openIncidents: openIncidents || 0,
        occupiedUnits: occupiedUnits || 0,
        monthlyPackages: monthlyPackages || 0
      };
    },
    enabled: !!profile?.condominium_id,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};